import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';
import { BASE_URL } from '../../src/apis/constant'
import pushToDataLayer from '../apis/GTM';

export const useAppStore = create(
  persist(
    (set, get) => ({
      // Current page state
      currentPage: 'shop',

      // Active drawer state
      activeDrawer: null,

      // Product drawer state
      productDrawer: {
        open: false,
        product: null
      },


      // Wishlist state  
      wishlistItems: [],
      wishlistCount: 0,





      // Cart state
      cartItems: [],
      cartCount: 0,
      cartApiLoading: false,
      setCartApiLoading: (value) => set({ cartApiLoading: value }),

      // ==================== CART ACTIONS ====================

      fetchCartFromBackend: async () => {

        const { setCartApiLoading } = get();

        try {
          setCartApiLoading(true);
          const customerId = localStorage.getItem('customerId');
          if (!customerId) {
            console.log('No customer ID found in localStorage');
            return { cartItems: [], cartCount: 0 };
          }

          const response = await axios.post(`${BASE_URL}/api/get_cart_by_cusid/`, {
            customer_id: customerId
          });

          if (response.data) {
            const cartItems = response.data.cart_items.map(item => ({
              id: String(item.id),
              productId: String(item.productId),
              name: item.name,
              variantType: item.variantType,
              variantValue: item.variantValue,
              variantId: item.variantId ? String(item.variantId) : null,
              price: item.price,
              offerPrice: item.offerPrice || item.price,
              image: item.image ? `${BASE_URL}${item.image}` : '',
              quantity: item.quantity,
              stock: item.stock || 10
            }));

            return {
              cartItems,
              cartCount: response.data.cart_count || 0
            };
          }

          return { cartItems: [], cartCount: 0 };
        } catch (error) {
          console.error('Error fetching cart:', error);
          return { cartItems: [], cartCount: 0 };
        } finally {
          setCartApiLoading(false);
        }
      },

      // Initialize cart - fetches from API
      initializeCart: async () => {
        const { cartItems, cartCount } = await get().fetchCartFromBackend();
        set({
          cartItems: cartItems || [],
          cartCount: cartCount || 0
        });
      },

      // Keep existing mock implementations for other actions for now
      updateCartItemQuantity: (productId, newQuantity) => {
        set((state) => {
          const updatedItems = state.cartItems.map(item =>
            item.id === productId
              ? { ...item, quantity: newQuantity }
              : item
          );

          const newCount = updatedItems.reduce((total, item) => total + (item.quantity || 1), 0);

          // TODO: Implement API sync later
          console.log('TODO: Sync quantity update to API', { productId, newQuantity });

          return {
            cartItems: updatedItems,
            cartCount: newCount
          };
        });
      },

      removeFromCart: (productId) => {
        set((state) => {
          const newItems = state.cartItems.filter(item => item.id !== productId);
          const newCount = newItems.reduce((total, item) => total + (item.quantity || 1), 0);

          return {
            cartItems: newItems,
            cartCount: newCount
          };
        });
      },


      // ==================== SIMPLE ACTIONS ====================

      setCurrentPage: (page) => set({ currentPage: page }),

      openDrawer: (drawerName) => set({ activeDrawer: drawerName }),
      closeDrawer: () => set({ activeDrawer: null }),

      // Product drawer actions
      openProductDrawer: (product) => set({
        productDrawer: { open: true, product }
      }),

      closeProductDrawer: () => set({
        productDrawer: { open: false, product: null }
      }),

      closeProductDrawerForModal: () => set((state) => ({
        productDrawer: {
          open: false,
          product: state.productDrawer.product // Keep product reference
        }
      })),

      isAnyDrawerOpen: () => !!get().activeDrawer || get().productDrawer.open,

      // ==================== WISHLIST ACTIONS ====================

      toggleWishlist: (product) => set((state) => {
        const exists = state.wishlistItems.some(item => item.id === product.id);

        if (exists) {
          const newItems = state.wishlistItems.filter(item => item.id !== product.id);
          return {
            wishlistItems: newItems,
            wishlistCount: newItems.length
          };
        } else {
          const newItems = [...state.wishlistItems, product];


          /* ------------------------ GTM Configuration ------------------------ */
          pushToDataLayer('add_to_wishlist',
            {
              value: (product.discountPrice || product.price).toFixed(2),
              currency: 'BDT',
              content_name: product.name,
              content_ids: [String(product.id)],
              content_type: 'product'
            },
            {
              external_id: localStorage.getItem("customerId")
            }
          );
          /* ------------------------ GTM Configuration ------------------------ */


          return {
            wishlistItems: newItems,
            wishlistCount: newItems.length
          };
        }
      }),

      addToWishlist: (product) => set((state) => {
        const exists = state.wishlistItems.some(item => item.id === product.id);
        if (!exists) {
          const newItems = [...state.wishlistItems, product];

          return {
            wishlistItems: newItems,
            wishlistCount: newItems.length
          };
        }
        return state;
      }),

      removeFromWishlist: (productId) => set((state) => {
        const newItems = state.wishlistItems.filter(item => item.id !== productId);
        return {
          wishlistItems: newItems,
          wishlistCount: newItems.length
        };
      }),

      clearWishlist: () => set({
        wishlistItems: [],
        wishlistCount: 0
      }),



      // ==================== UTILITY GETTERS ====================
      isInCart: (productId) => {
        return get().cartItems.some(item => item.id === productId);
      },


      isInWishlist: (productId) => {
        return get().wishlistItems.some(item => item.id === productId);
      },


      getCartTotal: () => {
        const { cartItems } = get();
        return cartItems.reduce((total, item) => {
          const price = Number(item.price) || 0;
          const offerPrice = item.offerPrice ? Number(item.offerPrice) : price;
          const quantity = Number(item.quantity) || 1;
          return total + (offerPrice * quantity);
        }, 0);
      },

      getCartItemCount: (productId) => {
        const item = get().cartItems.find(item => item.id === productId);
        return item ? item.quantity : 0;
      }
    }),
    {
      name: 'app-storage',
      partialize: (state) => ({
        cartItems: state.cartItems,
        cartCount: state.cartCount,
        wishlistItems: state.wishlistItems,
        wishlistCount: state.wishlistCount,
        currentPage: state.currentPage
      })
    }



  )
);



export const useFilterControl = create((set, get) => ({
  appliedFilters: {
    categories: [],
    priceRange: [0, 5000]
  },


  setFilters: (filters) => {
    set({
      appliedFilters: {
        ...get().appliedFilters,
        ...filters
      }
    });
  },

  resetFilters: () => {
    set({
      appliedFilters: {
        categories: [],
        priceRange: [0, 5000]
      }
    });
  },

  // Helper to get current filters
  getFilters: () => {
    return get().appliedFilters;
  },


  hasAppliedFilters: () => {
    const { categories, priceRange } = get().appliedFilters;
    const [minPrice, maxPrice] = priceRange;

    return (
      categories.length > 0 ||
      minPrice !== 0 ||
      maxPrice !== 5000
    );
  },


}));





// export const useCheckoutStore = create((set, get) => ({
//   checkoutItems: [],

//   lastCheckoutVisit: '',

//   setLastCheckoutVisit: () => {
//     const now = new Date().toISOString();
//     set({ lastCheckoutVisit: now });
//     return now;
//   },

//   checkoutDuration: () => {
//     const { lastCheckoutVisit } = get();
//     if (!lastCheckoutVisit) return 0;

//     const lastVisit = new Date(lastCheckoutVisit);
//     const now = new Date();
//     return Math.floor((now - lastVisit) / 1000);
//   },


//   setCheckoutItems: (items) =>
//     set({ checkoutItems: items }),

//   // addCheckoutItem: (item) =>
//   //   set((state) => ({
//   //     checkoutItems: [...state.checkoutItems, item],
//   //   })),

//   // clearCheckoutItems: () =>
//   //   set({ checkoutItems: [] }),

//   hasCheckoutItems: () =>
//     get().checkoutItems.length > 0,


//   showAddToCartModal: false,
//   setShowAddToCartModal: (value) => set({ showAddToCartModal: value }),


//   orderId: null,
//   setOrderId: (id) => set({ orderId: id }),
//   clearOrderId: () => set({ orderId: null }),



// }));





export const useCheckoutStore = create(
  persist(
    (set, get) => ({
      checkoutItems: [],
      isFlowActive: false,
      orderId: null,
      lastCheckoutVisit: '',

      showAddToCartModal: false,

      setCheckoutItems: (items) =>
        set({ checkoutItems: items, isFlowActive: true }),

      clearCheckout: () =>
        set({ checkoutItems: [], isFlowActive: false, orderId: null }),

      setShowAddToCartModal: (value) => set({ showAddToCartModal: value }),

      setOrderId: (id) => set({ orderId: id }),

      setLastCheckoutVisit: () => {
        const now = new Date().toISOString();
        set({ lastCheckoutVisit: now });
        return now;
      },

      checkoutDuration: () => {
        const { lastCheckoutVisit } = get();
        if (!lastCheckoutVisit) return 0;

        const lastVisit = new Date(lastCheckoutVisit);
        const now = new Date();
        return Math.floor((now - lastVisit) / 1000);
      },

      hasCheckoutItems: () => get().checkoutItems.length > 0,
    }),
    {
      name: 'checkout-storage',
      storage: createJSONStorage(() => sessionStorage),


      partialize: (state) => ({
        checkoutItems: state.checkoutItems,
        isFlowActive: state.isFlowActive,
        orderId: state.orderId,
      }),
    }
  )
);






























export const useAdminControl = create((set, get) => ({

  isSidebarOpen: true,

  currentPage: '',

  toggleSidebar: () => set((state) => ({
    isSidebarOpen: !state.isSidebarOpen
  })),

  setCurrentPage: (page) => set({ currentPage: page }),



  // Controlling drawers
  activeDrawer: null,
  selectedProduct: null,

  openDrawer: (drawerName) => set({ activeDrawer: drawerName }),

  closeDrawer: () => set({ activeDrawer: null, selectedProduct: null }),



  setSelectedProduct: (product) => set({ selectedProduct: product }),



  // Rendering Controll

  renderCount: 0,
  reRender: () => { set((state) => ({ renderCount: state.renderCount + 1 })) },





}));